import React, { useState, useEffect } from "react";
import { Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "config/config";
import "./FolderCard.scss";
import { FolderIconSmall } from "@assets/icons/FolderIconSmall";

const FolderCard = ({ folder }) => {
  const navigate = useNavigate();
  const [folderStats, setFolderStats] = useState({
    documentsCount: 0,
    subfoldersCount: 0,
    totalSize: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch folder statistics
  useEffect(() => {
    const fetchFolderStats = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${BASE_URL}folders/${folder._id}/content`);
        const content = response?.data?.data || { folders: [], documents: [] };
        
        setFolderStats({
          documentsCount: content.documents?.length || 0,
          subfoldersCount: content.folders?.length || 0,
          totalSize: content.documents?.reduce((total, doc) => total + (doc.size || 0), 0) || 0
        });
      } catch (error) {
        console.error('Error fetching folder stats:', error);
        setFolderStats({
          documentsCount: 0,
          subfoldersCount: 0,
          totalSize: 0
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchFolderStats();
  }, [folder._id]);

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Hier';
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    if (diffDays < 30) return `Il y a ${Math.ceil(diffDays / 7)} semaines`;
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  };

  return (
    <div className="folder-card-container">
      <Card
        className="card-folder"
        onClick={() => navigate(`/folder/${folder._id}`)}
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            navigate(`/folder/${folder._id}`);
          }
        }}>
        <Card.Body className="folder-content">
          <div className="folder-header">
            <div className="folder-icon">
              <FolderIconSmall />
            </div>
            <div className="folder-actions">
              <button 
                className="action-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  // Add folder actions here
                }}
                title="Plus d'options"
              >
                <i className="fas fa-ellipsis-v"></i>
              </button>
            </div>
          </div>
          
          <div className="folder-info">
            <h4 className="folder-title" title={folder.name}>
              {folder.name}
            </h4>
            <div className="folder-meta">
              <span className="meta-item">
                <i className="fas fa-calendar-alt me-1"></i>
                {formatDate(folder.updatedAt || folder.createdAt)}
              </span>
            </div>
          </div>

          <div className="folder-stats">
            {isLoading ? (
              <div className="loading-stats">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
              </div>
            ) : (
              <>
                <div className="stat-row">
                  <div className="stat-item">
                    <i className="fas fa-file-alt"></i>
                    <span className="stat-value">{folderStats.documentsCount}</span>
                    <span className="stat-label">fichiers</span>
                  </div>
                  <div className="stat-item">
                    <i className="fas fa-folder"></i>
                    <span className="stat-value">{folderStats.subfoldersCount}</span>
                    <span className="stat-label">dossiers</span>
                  </div>
                </div>
                {folderStats.totalSize > 0 && (
                  <div className="stat-row">
                    <div className="stat-item stat-size">
                      <i className="fas fa-hdd"></i>
                      <span className="stat-value">{formatFileSize(folderStats.totalSize)}</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default FolderCard;
